/**
 * Видео.
 *
 * Отличается от картинки тремя вещами, и каждая из них — источник
 * отдельных бед в эфире: оно ИГРАЕТ (значит, тратит процессор
 * стримера), ЗВУЧИТ (значит, обязано идти через общий мастер-гейн)
 * и КОНЧАЕТСЯ (значит, надо решить, что показывать потом).
 */
import { audioGraph } from './audio.js';
import { asRecord, numberInRange, oneOf } from './props.js';
const TYPE = 'video';
/**
 * Насколько копия вправе отстать, прежде чем её подтянут.
 *
 * Перемотка не бесплатна: браузер ищет ключевой кадр, картинка
 * дёргается, звук щёлкает. Сотню миллисекунд разницы не видит никто,
 * а полсекунды — видно, и вот её уже стоит чинить рывком.
 */
const SYNC_TOLERANCE_MS = 500;
/** Как видео вписывается в отведённый прямоугольник. */
const FITS = ['contain', 'cover', 'fill'];
/*
ПОРЯДОК ИСТОЧНИКОВ: WEBM ПЕРЕД MP4, и это не мелочь.

Оба файла делает наш воркер (задача 4.1). webm с VP9 умеет
прозрачность, mp4 с H.264 — нет вовсе. Поставь mp4 первым, и ролик
с прозрачным фоном приедет в эфир чёрным прямоугольником поверх
игры: браузер возьмёт первый, который умеет играть, а Chromium
умеет оба.

Зачем тогда mp4. Оверлей — это Chromium, но ПАНЕЛЬ открывают
в чём угодно, включая Safari, где поддержка webm неполная. mp4
там и пригодится. Выбирает браузер сам, знания «нужна ли этому
ролику прозрачность» ни у кого нет — и заводить такую галочку
значило бы спрашивать у человека то, чего он про свой файл не знает.
*/
const PREFERRED = ['video/webm', 'video/mp4'];
export const videoWidget = {
    type: TYPE,
    parse(raw) {
        const props = asRecord(TYPE, raw);
        return {
            sources: sourcesOf(props),
            loop: props.loop !== false,
            autoplay: props.autoplay !== false,
            hideOnEnd: props.hideOnEnd === true,
            volume: numberInRange(TYPE, props, 'volume', 0, 1, 1),
            fit: oneOf(TYPE, props, 'fit', FITS, 'contain'),
            opacity: numberInRange(TYPE, props, 'opacity', 0, 1, 1),
        };
    },
    mount(props) {
        const el = document.createElement('div');
        el.dataset.widget = TYPE;
        el.style.overflow = 'hidden';
        const video = document.createElement('video');
        // playsinline: без него часть браузеров разворачивает видео
        // на весь экран. В оверлее это означает чужую картинку поверх
        // всего кадра
        video.setAttribute('playsinline', '');
        video.setAttribute('disablepictureinpicture', '');
        // ГРОМКОСТЬ ЭЛЕМЕНТА ОСТАЁТСЯ ЕДИНИЦЕЙ НАВСЕГДА.
        //
        // Это не забывчивость, а условие работы: всё, что мы приглушаем
        // здесь, прошло бы мимо мастер-гейна владельца. Тише делает
        // узел графа, и только он. Тест сверяет именно это.
        video.volume = 1;
        // muted тоже нельзя: он глушит и то, что уходит в Web Audio,
        // — граф получил бы тишину
        video.muted = false;
        video.style.width = '100%';
        video.style.height = '100%';
        video.style.display = 'block';
        el.append(video);
        // Звук включается В ГРАФ, а не оставляется элементу.
        //
        // null означает, что графа нет вовсе — в предпросмотре панели
        // его и не создают. Слой тогда молчит, и это правильно: звук
        // ролика, начавшийся в панели, услышит только модератор,
        // а испугается он по-настоящему
        let route = audioGraph()?.connect(video) ?? null;
        if (route === null) {
            // Дороги в граф нет — значит и звучать нечему. Глушим
            // элемент: иначе он играл бы мимо мастер-гейна, то есть
            // мимо потолка владельца
            video.muted = true;
        }
        const onEnded = () => {
            el.dataset.ended = 'true';
            if (props.hideOnEnd) {
                // visibility, а не display: display перекладывает страницу
                // заново, а видимость меняется на видеокарте
                video.style.visibility = 'hidden';
            }
        };
        const onPlaying = () => {
            delete el.dataset.ended;
            video.style.visibility = 'visible';
        };
        /*
          НЕЗАГРУЗИВШИЙСЯ РОЛИК ОБЯЗАН БЫТЬ ВИДИМЫМ — той же отметкой,
          что и у картинки (image.ts).
    
          Причина у ролика та же и даже острее: файл могли удалить
          из библиотеки канала, пока слой на него ссылается. В эфире
          на его месте будет пустота, слой в списке при этом выглядит
          обычным, и стример узнаёт о поломке от зрителей.
    
          `error` на самом элементе, а не на каждом <source>: когда
          источников несколько, браузер перебирает их сам и объявляет
          неудачу один раз — когда не подошёл НИ ОДИН.
        */
        const onFailed = () => {
            el.dataset.failed = 'true';
        };
        const onLoaded = () => {
            delete el.dataset.failed;
        };
        video.addEventListener('ended', onEnded);
        video.addEventListener('playing', onPlaying);
        video.addEventListener('error', onFailed);
        video.addEventListener('loadeddata', onLoaded);
        // fresh: этот вызов — создание слоя, и автозапуску здесь место.
        // Дальше свойства применяются на каждую правку сцены, и запускать
        // ролик там нельзя — см. apply()
        apply(el, video, props, route, true);
        return {
            el,
            update(next) {
                apply(el, video, next, route);
            },
            /*
              ПРИВЕДЕНИЕ К ОБЩЕМУ СОСТОЯНИЮ.
      
              Не команда, а состояние: сервер держит отметку «ролик на
              такой-то позиции и играет», и каждая копия подтягивается
              к ней сама — при подключении, при чужой команде и просто
              по времени, если отстала.
      
              ДОПУСК ОБЯЗАТЕЛЕН. Перемотка не бесплатна: браузер ищет
              ключевой кадр, картинка дёргается, звук щёлкает. Подтягивать
              копию, отставшую на сотню миллисекунд, значило бы дёргать
              эфир постоянно и без пользы: такой разницы не видит никто.
            */
            syncPlayback(target) {
                const behind = Math.abs(video.currentTime * 1000 - target.positionMs);
                if (behind > SYNC_TOLERANCE_MS) {
                    delete el.dataset.ended;
                    video.style.visibility = 'visible';
                    video.currentTime = Math.max(0, target.positionMs) / 1000;
                }
                // Играть или стоять решает состояние, а не то, что делал
                // проигрыватель до сих пор: разошлись — значит приводим
                if (target.playing && video.paused) {
                    start(el, video);
                    return;
                }
                if (!target.playing && !video.paused)
                    video.pause();
            },
            command(name, positionMs) {
                if (name === 'pause') {
                    video.pause();
                    return;
                }
                /*
                  ПЕРЕМОТКА НЕ ЗАПУСКАЕТ ОСТАНОВЛЕННОЕ.
        
                  Она отвечает на вопрос «где», а не «играть ли». Ролик,
                  поставленный на паузу, после перемотки обязан остаться
                  на паузе — иначе одним движением ползунка человек вернул бы
                  в эфир то, что только что оттуда убрал.
        
                  Замерший последний кадр при этом снимается: он про конец
                  ролика, а перемотка уводит с конца.
                */
                if (name === 'seek') {
                    delete el.dataset.ended;
                    video.style.visibility = 'visible';
                    video.currentTime = (positionMs ?? 0) / 1000;
                    return;
                }
                if (name === 'restart') {
                    // Замерший последний кадр снимается вместе с перезапуском:
                    // иначе «сначала» на закончившемся ролике оставляло бы
                    // его спрятанным (см. hideOnEnd)
                    delete el.dataset.ended;
                    video.style.visibility = 'visible';
                    video.currentTime = 0;
                }
                /*
                  НАЖАТИЕ В ПАНЕЛИ — ЭТО РЕШЕНИЕ ЧЕЛОВЕКА, а не автозапуск.
                  Если ролик играет без звука только потому, что браузер
                  не пустил его звучащим, здесь заглушку снимаем: раз просят
                  играть — просят и слышать. Откажет снова — `start` вернёт
                  беззвучное воспроизведение, и ролик останется в кадре.
                */
                if (el.dataset.autoMuted === 'true') {
                    delete el.dataset.autoMuted;
                    video.muted = false;
                }
                start(el, video);
            },
            destroy() {
                video.removeEventListener('ended', onEnded);
                video.removeEventListener('playing', onPlaying);
                video.removeEventListener('error', onFailed);
                video.removeEventListener('loadeddata', onLoaded);
                // ОСТАНОВИТЬ И РАЗОРВАТЬ — обязательно и в таком порядке.
                //
                // Видео, оставленное живым за кадром, продолжает
                // декодироваться и играть, съедая процессор стримера ровно
                // во время эфира. А узлы Web Audio не собираются сборщиком
                // мусора, пока подключены, — за часы работы их набирается
                // столько, сколько было слоёв
                video.pause();
                video.removeAttribute('src');
                while (video.firstChild)
                    video.firstChild.remove();
                video.load();
                route?.release();
                route = null;
                el.remove();
            },
        };
    },
    /**
     * Готовит метаданные, не создавая слоя.
     *
     * ТОЛЬКО метаданные (`preload="metadata"`, docs/04): скачать ролик
     * целиком ради слоя, который стоит за кадром и, может быть,
     * никогда не понадобится, значит потратить полосу стримера
     * во время эфира.
     */
    preload(props) {
        const first = props.sources[0];
        if (first === undefined)
            return;
        const probe = document.createElement('video');
        probe.preload = 'metadata';
        probe.src = first.src;
    },
};
/**
 * Запускает ролик, а если браузер не пустил — запускает БЕЗ ЗВУКА.
 *
 * ЭТО ПОЧИНКА «В OBS ВИДЕО НЕ ПОКАЗЫВАЕТСЯ, А В ПАНЕЛИ ПОКАЗЫВАЕТСЯ».
 *
 * Разница между ними ровно одна: в панели графа звука нет, элемент
 * там заглушён при создании (см. mount) — и беззвучный автозапуск
 * разрешён везде. В оверлее граф есть, элемент звучит, а звучащий
 * автозапуск браузер не пускает без жеста человека. Жеста в Browser
 * Source не бывает вовсе: OBS умеет его подменять настройкой
 * источника, но пока она не выставлена, `play()` отклоняется — и слой
 * остаётся пустым прямоугольником. Стример при этом видит ролик
 * в панели и не понимает, почему его нет в эфире.
 *
 * Раньше отказ только помечался в DOM (`data-blocked`), и отметку
 * эту не читал никто.
 *
 * ВИДИМЫЙ И МОЛЧАЩИЙ ЛУЧШЕ, ЧЕМ ЗВУЧАЩИЙ И ОТСУТСТВУЮЩИЙ. Тем более
 * что слой и так кладётся с нулевой громкостью: звук у ролика
 * включают осознанно, и до этого момента терять нечего.
 *
 * ЗАГЛУШКА ПОМЕЧАЕТСЯ (`data-auto-muted`), чтобы её можно было снять,
 * когда человек ЯВНО попросит играть: нажатие в панели — это уже
 * решение, а не автозапуск. Если браузер и тогда откажет, ролик
 * вернётся к беззвучному воспроизведению, а не встанет.
 */
/* eslint-disable no-param-reassign --
   Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
   ровно то, ради чего функция и существует. Тот же случай,
   что в apply() ниже. */
function start(el, video) {
    const played = () => {
        delete el.dataset.blocked;
    };
    void video.play().then(played, () => {
        el.dataset.blocked = 'true';
        // Уже без звука, а всё равно не пустил: помочь нечем.
        // Настаивать здесь — значит зациклиться
        if (video.muted)
            return;
        video.muted = true;
        el.dataset.autoMuted = 'true';
        void video.play().then(played, () => { });
    });
}
/* eslint-enable no-param-reassign */
/** Разбирает источники: карта «тип → адрес» либо готовый список. */
function sourcesOf(props) {
    const raw = props.sources;
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
        return [];
    }
    const map = raw;
    const out = [];
    // Сперва известные типы в нашем порядке, потом всё остальное:
    // незнакомый формат не должен оказаться раньше webm и увести
    // ролик с прозрачностью в чёрный прямоугольник
    const seen = new Set();
    for (const type of PREFERRED) {
        const src = map[type];
        if (typeof src === 'string' && src !== '') {
            out.push({ src, type });
            seen.add(type);
        }
    }
    for (const [type, src] of Object.entries(map)) {
        if (seen.has(type))
            continue;
        if (typeof src === 'string' && src !== '') {
            out.push({ src, type });
        }
    }
    return out;
}
/**
 * Переносит свойства в существующие узлы.
 *
 * ИСТОЧНИКИ ПЕРЕСОБИРАЮТСЯ ТОЛЬКО ПРИ ИЗМЕНЕНИИ: пересоздание
 * <source> и вызов load() начинают воспроизведение сначала. Для
 * ролика, который сейчас идёт в эфире, это рывок на ровном месте.
 */
function apply(el, video, props, route, 
/** Слой только что создан: воспроизведение ещё не начиналось. */
fresh = false) {
    /* eslint-disable no-param-reassign --
       Правило бережёт неизменяемость СОСТОЯНИЯ, а здесь меняется DOM —
       ровно то, ради чего функция и существует. Тот же случай,
       что в image.ts. */
    const wanted = props.sources.map((s) => `${s.type}|${s.src}`).join(',');
    const rebuilt = video.dataset.sources !== wanted;
    if (rebuilt) {
        video.dataset.sources = wanted;
        while (video.firstChild)
            video.firstChild.remove();
        for (const source of props.sources) {
            const node = document.createElement('source');
            node.setAttribute('src', source.src);
            node.setAttribute('type', source.type);
            video.append(node);
        }
        video.load();
    }
    video.loop = props.loop;
    video.autoplay = props.autoplay;
    /*
      АВТОЗАПУСК — ЭТО ПРО НАЧАЛО, А НЕ ПРО КАЖДУЮ ПРАВКУ.
  
      Здесь стоял безусловный `play()` на каждое применение свойств,
      и это ломало ровно то, ради чего слоем управляют:
  
        * ролик на ПАУЗЕ возобновлялся, стоило подвинуть слой мышью —
          пауза отменялась движением, к ней не относящимся;
        * ЗАКОНЧИВШИЙСЯ ролик начинался заново: `play()` на дошедшем
          до конца элементе перематывает его в ноль. А во время
          перетаскивания правки приходят десятки раз в секунду —
          ролик перезапускался непрерывно, пока слой держали мышью.
  
      Свойства применяются и при каждом чужом движении, и при каждой
      дельте сцены. Запускать надо ровно там, где воспроизведение
      ещё не начиналось: при создании слоя и при СМЕНЕ ИСТОЧНИКА —
      после `load()` элемент пуст, и автозапуск обязан сработать снова.
      Всё остальное — дело проигрывателя и явных команд модератора.
    */
    if (props.autoplay && (rebuilt || fresh))
        start(el, video);
    // Громкость — В ГРАФ. Элемент остаётся на единице: см. audio.ts
    route?.setGain(props.volume);
    video.style.objectFit = props.fit;
    el.style.opacity = String(props.opacity);
    /* eslint-enable no-param-reassign */
}
//# sourceMappingURL=video.js.map